import {
  Box,
  Grid,
  makeStyles,
  Sidebar,
  SidebarItemLink,
  Skeleton,
  Theme,
  useMediaQuery,
  useTheme,
} from '@freighthub/silhouette-core'
import { NewQuoteButtonRoot } from 'features/BookingTemplates'
import { useObserver } from 'mobx-react-lite'
import { FC } from 'react'
import { useAuth } from 'service/useAuth'
import { useUnleashFeatureFlags } from 'service/useUnleashFeatureFlags'

import { LOGO_URL_DARK_SVG } from '../logoUrls'
import { useMenuItems } from './LeftBar'
import { isRouteItemActive, NavItem } from './NavItem'

export const testIds = {
  sidebar: 'sidebar-nav-root',
  item: 'sidebar-nav-item',
  skeleton: 'sidebar-nav-skeleton',
}

const useSkeletonStyles = makeStyles<Theme>((theme) => ({
  item: {
    alignItems: 'center',
    height: 48,
  },
  icon: {
    padding: theme.spacing(0, 2),
  },
  text: {
    paddingRight: theme.spacing(3),
    flex: 1,
  },
}))

type SidebarItemSkeletonProps = {
  expanded: boolean
}

const SidebarItemSkeleton: FC<SidebarItemSkeletonProps> = ({ expanded }) => {
  const classes = useSkeletonStyles()
  return (
    <Box my={4} data-testid={testIds.skeleton}>
      {[...Array(8)].map((_, index) => {
        return (
          <Grid container className={classes.item} key={`SidebarSkeletonItem-${index}`}>
            <Grid className={classes.icon}>
              <Skeleton variant="circle" width={28} height={28} />
            </Grid>
            {expanded && (
              <Grid className={classes.text}>
                <Skeleton variant="rect" height={28} />
              </Grid>
            )}
          </Grid>
        )
      })}
    </Box>
  )
}

type StyleProps = { expanded: boolean }

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  sidebar: {
    position: 'static',
    justifyContent: 'space-between',
  },
  button: ({ expanded }) => ({
    margin: theme.spacing(2, 1.5),
    ...(expanded ? { marginRight: 'auto' } : {}),
  }),
  logo: {
    display: 'none',
    height: 25,
    margin: `${theme.spacing(3)}px auto`,
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
}))

export interface SidebarNavProps {
  open: boolean
  onClose: () => void
}

export const SidebarNav: FC<SidebarNavProps> = ({ open, onClose, ...other }) => {
  const {
    state: { customerLoaded },
  } = useAuth()
  const theme = useTheme<Theme>()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const expanded: boolean = isSmallScreen || open
  const classes = useStyles({ expanded })
  const { getMenuItems } = useMenuItems()
  const { isFFLoading } = useUnleashFeatureFlags()

  const handleClickItem = (item: NavItem) => {
    if (item.onClick) {
      item.onClick()
    }

    if (isSmallScreen) {
      onClose()
    }
  }

  return useObserver(() => (
    <Sidebar
      variant="permanent"
      expandedWidth={240}
      open={expanded}
      onClose={onClose}
      classes={{ paper: classes.sidebar }}
      data-testid={testIds.sidebar}
      {...other}
    >
      <nav data-userlane-id="sidebar">
        {customerLoaded && <NewQuoteButtonRoot expanded={expanded} className={classes.button} />}
        {customerLoaded && !isFFLoading ? (
          getMenuItems().map((item) => {
            const selected = isRouteItemActive(item)
            const Icon = selected && !!item.selectedIcon ? item.selectedIcon : item.icon

            return (
              <SidebarItemLink
                key={item.tooltip}
                to={item.href}
                icon={<Icon />}
                label={item.tooltip}
                selected={selected}
                showLabel={expanded}
                onClick={() => handleClickItem(item)}
                BadgeProps={{
                  invisible: !item.showBadge,
                  variant: 'dot',
                }}
                data-testid={testIds.item}
                data-userlane-id={item.userlaneId}
              />
            )
          })
        ) : (
          <SidebarItemSkeleton expanded={expanded} />
        )}
      </nav>
      <img src={LOGO_URL_DARK_SVG} className={classes.logo} alt="forto-logo" />
    </Sidebar>
  ))
}
